import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const { user, isAuthenticated } = useAuth();

  const location = useLocation();

  // Callback refs for event listeners
  const [notificationListeners, setNotificationListeners] = useState([]);
  const [interestListeners, setInterestListeners] = useState([]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!isAuthenticated || !user) {
      // Disconnect if user logs out
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    // Determine backend URL
    let SOCKET_URL;
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      SOCKET_URL = 'http://localhost:5000';
    } else {
      // Production - hardcode the API URL for WebSocket
      SOCKET_URL = 'https://api.realtimerepairs.com';
    }

    // Create Socket.IO connection
    const newSocket = io(SOCKET_URL, {
      auth: {
        token: token
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    // Connection event handlers
    newSocket.on('connect', () => {
      setConnected(true);
    });

    newSocket.on('connected', (data) => {
      // WebSocket confirmed
    });

    newSocket.on('disconnect', (reason) => {
      setConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      newSocket.disconnect();
    };
  }, [isAuthenticated, user]);

  // Set up event listeners
  useEffect(() => {
    if (!socket) return;

    // Listen for new notifications
    const handleNewNotification = (notification) => {
      notificationListeners.forEach(listener => listener(notification));
    };

    // Listen for notification count updates
    const handleUnreadCount = (data) => {
      setNotificationCount(data.count);
    };

    // Listen for new job interest
    const handleNewInterest = (interest) => {
      interestListeners.forEach(listener => listener(interest));
    };

    // Show toast for new messages (unless user is on messages page)
    const handleNewMessage = (data) => {
      if (!location.pathname.startsWith('/messages')) {
        toast.info(`New message from ${data.senderName || 'someone'}`, {
          position: 'top-right',
          autoClose: 5000,
          onClick: () => { window.location.href = `/messages`; }
        });
      }
    };

    // Show toast for new bids on homeowner's jobs
    const handleNewBid = (data) => {
      toast.info(`New bid on "${data.jobTitle || 'your job'}" - $${data.amount || ''}`, {
        position: 'top-right',
        autoClose: 6000,
        onClick: () => { window.location.href = `/jobs/${data.jobId}`; }
      });
    };

    // Show toast when a bid is accepted (for contractors)
    const handleBidAccepted = (data) => {
      toast.success(`Your bid on "${data.jobTitle || 'a job'}" was accepted!`, {
        position: 'top-right',
        autoClose: 8000,
        onClick: () => { window.location.href = `/jobs/${data.jobId}`; }
      });
    };

    socket.on('notification:new', handleNewNotification);
    socket.on('notifications:unread', handleUnreadCount);
    socket.on('interest:new', handleNewInterest);
    socket.on('message:new', handleNewMessage);
    socket.on('bid:new', handleNewBid);
    socket.on('bid:accepted', handleBidAccepted);

    return () => {
      socket.off('notification:new', handleNewNotification);
      socket.off('notifications:unread', handleUnreadCount);
      socket.off('interest:new', handleNewInterest);
      socket.off('message:new', handleNewMessage);
      socket.off('bid:new', handleNewBid);
      socket.off('bid:accepted', handleBidAccepted);
    };
  }, [socket, notificationListeners, interestListeners, location.pathname]);

  // Subscribe to notifications
  const onNewNotification = useCallback((callback) => {
    setNotificationListeners(prev => [...prev, callback]);
    return () => {
      setNotificationListeners(prev => prev.filter(cb => cb !== callback));
    };
  }, []);

  // Subscribe to job interests
  const onNewInterest = useCallback((callback) => {
    setInterestListeners(prev => [...prev, callback]);
    return () => {
      setInterestListeners(prev => prev.filter(cb => cb !== callback));
    };
  }, []);

  const value = {
    socket,
    connected,
    notificationCount,
    setNotificationCount,
    onNewNotification,
    onNewInterest
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
